import { PdfLayout } from '../PdfLayout';
import { ScanEye, Layers, Crosshair, Bot, Repeat, Archive, Camera } from 'lucide-react';

const benefits = [
  {
    icon: ScanEye,
    title: 'A Full Inspection Toolbox',
    text: 'Circles, blobs, presence, color match, pattern match, caliper, circle gauge, printed-text verification, and barcode, QR and DataMatrix reading.',
  },
  {
    icon: Layers,
    title: 'Several Tools, One Verdict',
    text: 'All tools judge the same frame, across up to four cameras, for one pass tag. A pattern-match locator makes the others follow the part.',
  },
  {
    icon: Crosshair,
    title: 'Real Coordinates, Not Pixels',
    text: 'Calibrate with a ChArUco board, AprilTag or ArUco markers, a chessboard or hand-placed points; positions and gauges then read in perspective-corrected millimeters on that plane.',
  },
  {
    icon: Bot,
    title: 'Robot Guidance',
    text: 'Touch off three points with the robot, and part positions and angle are published in its own frame — a pick point as a tag.',
  },
  {
    icon: Repeat,
    title: 'Programs & Changeover',
    text: 'One program per product, switched by a PLC, script or recipe writing a tag.',
  },
  {
    icon: Archive,
    title: 'Failure Image Archive',
    text: 'Annotated failed-part images are kept within a disk budget; the Camera widget shows the latest.',
  },
  {
    icon: Camera,
    title: 'Use the Camera You Have',
    text: 'Keyence, Cognex In-Sight and SICK images over HTTP, USB cameras, RTSP/MJPEG streams or any HTTP snapshot. Inspect continuously or on a trigger.',
  },
];

export function MachineVision() {
  return (
    <PdfLayout
      category="Machine Vision"
      headline="Inspection Results as Tags with"
      headlineHighlight="Machine Vision"
      description="Vision runs as a driver on the appliance, so a count, a measurement or a pass/fail verdict is just another tag — alarmed, historized, charted and tracked in SPC like everything else, with no separate vision PC."
      accentColor="#dc2626"
      benefits={benefits}
    >
      <VisionMockup />
    </PdfLayout>
  );
}

function VisionMockup() {
  const accent = '#dc2626';
  const tools = [
    { name: 'holes', type: 'Circles', result: '24 / 24' },
    { name: 'cap', type: 'Color match', result: '97.4%' },
    { name: 'bore', type: 'Circle gauge', result: '12.012 mm' },
    { name: 'lot', type: 'Text', result: 'LOT4471' },
    { name: 'label', type: 'Barcodes', result: 'DataMatrix' },
  ];
  return (
    <div className="glass-panel p-3 w-full max-w-[280px]">
      <div className="flex items-center gap-2 mb-2 px-1">
        <div className="flex gap-1.5">
          <div className="w-2 h-2 rounded-full bg-status-fault/60" />
          <div className="w-2 h-2 rounded-full bg-status-idle/60" />
          <div className="w-2 h-2 rounded-full bg-status-running/60" />
        </div>
        <span className="text-[9px] text-text-muted">Vision — Station 4 Camera</span>
      </div>

      {/* Verdict */}
      <div className="glass-card p-2 mb-2 flex items-center justify-between">
        <span className="text-lg font-extrabold text-status-running leading-none">Pass</span>
        <span className="text-[7px] text-text-muted text-right">
          5 of 5 tools judged
          <br />
          program: bottle_500
        </span>
      </div>

      {/* Annotated image */}
      <div className="glass-card p-1.5 mb-2">
        <svg viewBox="0 0 240 130" className="w-full">
          <rect x="0" y="0" width="240" height="130" rx="3" fill="#1e293b" />
          {/* Part */}
          <rect x="30" y="18" width="150" height="96" rx="6" fill="#475569" />
          {/* Holes found (circles tool) */}
          {[0, 1, 2, 3, 4, 5].map((c) =>
            [0, 1].map((r) => (
              <circle key={`${c}-${r}`} cx={48 + c * 18} cy={34 + r * 18} r="5" fill="#0f172a" stroke="#16a34a" strokeWidth="1.2" />
            )),
          )}
          <rect x="38" y="24" width="112" height="38" fill="none" stroke="#3b82f6" strokeWidth="0.8" strokeDasharray="3,2" />
          {/* Bore (circle gauge) */}
          <circle cx="150" cy="90" r="14" fill="#0f172a" stroke="#16a34a" strokeWidth="1.2" />
          <text x="150" y="112" textAnchor="middle" fontSize="6" fill="#bbf7d0">Ø 12.012 mm</text>
          {/* Lot code */}
          <rect x="44" y="80" width="64" height="14" fill="none" stroke="#3b82f6" strokeWidth="0.8" strokeDasharray="3,2" />
          <text x="76" y="90" textAnchor="middle" fontSize="7" fill="#e2e8f0" fontFamily="monospace">LOT4471</text>
          {/* DataMatrix */}
          <rect x="196" y="22" width="26" height="26" fill="#e2e8f0" />
          {[0, 1, 2, 3, 4].map((i) => (
            <rect key={i} x={198 + ((i * 7) % 20)} y={24 + ((i * 11) % 20)} width="4" height="4" fill="#0f172a" />
          ))}
          <rect x="194" y="20" width="30" height="30" fill="none" stroke="#16a34a" strokeWidth="1" />
          {/* Datum axes */}
          <line x1="12" y1="122" x2="34" y2="122" stroke={accent} strokeWidth="1.2" />
          <line x1="12" y1="122" x2="12" y2="100" stroke="#16a34a" strokeWidth="1.2" />
          <text x="36" y="124" fontSize="5" fill={accent}>X</text>
          <text x="9" y="97" fontSize="5" fill="#16a34a">Y</text>
        </svg>
      </div>

      {/* Tool results */}
      <div className="glass-card p-2 mb-2">
        {tools.map((t) => (
          <div key={t.name} className="flex items-center gap-2 text-[7px] py-0.5">
            <span className="w-1.5 h-1.5 rounded-full bg-status-running shrink-0" />
            <span className="text-text-primary font-medium w-9">{t.name}</span>
            <span className="text-text-muted flex-1">{t.type}</span>
            <span className="text-text-primary tabular-nums">{t.result}</span>
          </div>
        ))}
      </div>

      {/* Robot frame tags */}
      <div className="grid grid-cols-3 gap-1.5">
        {[
          { tag: 'bore/robotX', value: '412.6' },
          { tag: 'bore/robotY', value: '-88.3' },
          { tag: 'bore/robotAngle', value: '12.4°' },
        ].map((r) => (
          <div key={r.tag} className="glass-card p-1.5 text-center">
            <div className="text-[9px] font-bold" style={{ color: accent }}>{r.value}</div>
            <div className="text-[6px] text-text-muted font-mono">{r.tag}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
